import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../Pages/LanguageContext"; // مسیر واقعی خودت
import { GetActiveProduct } from "../services/ProductService"; // مسیر رو اگه فرق داره تطبیق بده
import { GetAllCustomer } from "../services/CustomerService";
import { GetAllOrder } from "../services/OrderService";
import { GetUserByUserName } from "../services/UserService"; // همون سرویسی که توی Order.jsx هم استفاده شده
import { GetAllEmployee } from "../services/EmployeeService"; // برای گرفتن fullName از روی employeeId

// وضعیت سفارش (باید با enum سمت بک‌اند هماهنگ بمونه: Ordering.DataLayout.Enum.Ordering.OrderStatus)
// Registered = 0, Confirmed = 1
// اگه بعداً مقدار جدیدی به enum اضافه شد، فقط همینجا و در STATUS_COLORS / statusLabels اضافه کن
const ORDER_STATUS_MAP = {
    0: "registered",
    1: "confirmed",
};

function normalizeOrderStatus(raw) {
    if (raw == null) return "registered";
    if (typeof raw === "number") return ORDER_STATUS_MAP[raw] ?? "registered";
    if (typeof raw === "string" && /^\d+$/.test(raw)) return ORDER_STATUS_MAP[Number(raw)] ?? "registered";
    return String(raw).toLowerCase();
}

const TEXTS = {
    fa: {
        welcome: "خوش آمدید",
        company: "داشبورد مدیریت",
        monthlyRevenue: "درآمد این ماه",
        monthlyTarget: "هدف ماهانه",
        progress: "پیشرفت به هدف",
        stats: "آمار کلی",
        recentOrders: "سفارشات اخیر",
        //viewAll: "مشاهده همه →",
        noOrders: "سفارشی ثبت نشده",
        noOrdersSub: "اولین سفارش خود را ثبت کنید",
        products: "محصولات",
        orders: "سفارشات",
        statLabels: { productCount: "محصولات", orderCount: "سفارشات", customerCount: "مشتریان فعال" },
        statusLabels: { registered: "ثبت شده", confirmed: "تایید شده" },
        customers: "مشتریان",
        items: "آیتم",
    },
    ar: {
        welcome: "مرحباً",
        company: "لوحة الإدارة",
        monthlyRevenue: "إيرادات هذا الشهر",
        monthlyTarget: "الهدف الشهري",
        progress: "التقدم نحو الهدف",
        stats: "إحصائيات عامة",
        recentOrders: "الطلبات الأخيرة",
        //viewAll: "عرض الكل ←",
        noOrders: "لا توجد طلبات",
        noOrdersSub: "سجّل أول طلب لك",
        products: "المنتجات",
        orders: "الطلبات",
        statLabels: { productCount: "المنتجات", orderCount: "الطلبات", customerCount: "العملاء النشطون" },
        statusLabels: { registered: "مسجّل", confirmed: "مؤكَّد" },
        customers: "العملاء",
        items: "عنصر",
    },
};

const t = TEXTS.fa;

function useWidth() {
    const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
    useEffect(() => {
        const fn = () => setW(window.innerWidth);
        window.addEventListener("resize", fn);
        return () => window.removeEventListener("resize", fn);
    }, []);
    return w;
}

function useStats() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const [productsRes, customersRes, ordersRes] = await Promise.all([
                    GetActiveProduct(),
                    GetAllCustomer(),
                    GetAllOrder(),
                ]);

                const products = productsRes?.data ?? [];
                const customers = customersRes?.data ?? [];
                const orders = ordersRes?.data ?? [];

                // مشتریان فعال: چون GetAllCustomer فیلتر نمی‌کنه، خودمون روی isActive فیلتر می‌کنیم
                // اگه فیلد توی پاسخ IsActive (PascalCase) بود، این خط رو به c.IsActive تغییر بده
                const activeCustomers = customers.filter(c => c.isActive ?? c.IsActive);

                const now = new Date();
                const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

                const getOrderDate = (o) => new Date(o.orderDate ?? o.OrderDate);
                const getOrderTotal = (o) => o.totalPrice ?? o.TotalPrice ?? 0;

                const thisMonthOrders = orders.filter(o => {
                    const d = getOrderDate(o);
                    return d >= startOfThisMonth && d < startOfNextMonth;
                });
                const lastMonthOrders = orders.filter(o => {
                    const d = getOrderDate(o);
                    return d >= startOfLastMonth && d < startOfThisMonth;
                });

                const monthlyRevenue = thisMonthOrders.reduce((sum, o) => sum + getOrderTotal(o), 0);
                const lastMonthRevenue = lastMonthOrders.reduce((sum, o) => sum + getOrderTotal(o), 0);

                const revenueGrowth = lastMonthRevenue > 0
                    ? Math.round(((monthlyRevenue - lastMonthRevenue) / lastMonthRevenue) * 1000) / 10
                    : null;

                const orderCountDelta = lastMonthOrders.length > 0
                    ? Math.round(((thisMonthOrders.length - lastMonthOrders.length) / lastMonthOrders.length) * 100)
                    : null;

                if (alive) {
                    setStats({
                        productCount: products.length,
                        orderCount: orders.length,
                        orderCountDelta,
                        customerCount: activeCustomers.length,
                        monthlyRevenue,
                        revenueGrowth,
                    });
                }
            } catch (err) {
                if (alive) setError(err);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    return { stats, loading, error };
}

function useOrders({ limit = 5 } = {}) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await GetAllOrder();
                const list = res?.data ?? [];

                const getOrderDate = (o) => new Date(o.orderDate ?? o.OrderDate);

                const recent = [...list]
                    .sort((a, b) => getOrderDate(b) - getOrderDate(a))
                    .slice(0, limit)
                    .map(o => ({
                        id: o.orderNumber ?? o.OrderNumber ?? o.id ?? o.Id,
                        items: o.itemsCount ?? o.orderItems?.length ?? o.OrderItems?.length ?? 0,
                        total: o.totalPrice ?? o.TotalPrice ?? 0,
                        status: normalizeOrderStatus(o.status ?? o.Status),
                    }));

                if (alive) setOrders(recent);
            } catch (err) {
                if (alive) setError(err);
                if (alive) setOrders([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [limit]);

    return { orders, loading, error };
}

// از fullName کاربر، یک initials دو حرفی می‌سازه (حرف اول اسم + حرف اول فامیل)
// اگه اسم تک‌کلمه‌ای بود، همون دو حرف اول رو برمی‌گردونه
function getInitials(fullName) {
    if (!fullName || !fullName.trim()) return "?";
    const parts = fullName.trim().split(/\s+/);
    const raw = parts.length === 1
        ? parts[0].slice(0, 2)
        : (parts[0][0] || "") + (parts[parts.length - 1][0] || "");
    return raw.toUpperCase(); // برای اسم‌های لاتین (مثلاً userName انگلیسی) بزرگ نمایش داده بشه
}

// اطلاعات کاربر لاگین‌کرده را از localStorage (کلید "userName" که در LoginPage.jsx ذخیره می‌شود)
// می‌خواند. GetUserByUserName فقط id/userName/employeeId/language برمی‌گردونه (بدون fullName)
// پس با employeeId، اسم واقعی کارمند رو از GetAllEmployee پیدا می‌کنیم (همون الگوی Order.jsx).
function useUser() {
    const [user, setUser] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            const userName = localStorage.getItem("userName");
            if (!userName) return;
            try {
                const res = await GetUserByUserName(userName);
                const raw = res.data?.data ?? res.data;
                if (!raw || !alive) return;

                const employeeId = raw.employeeId ?? raw.EmployeeId ?? null;
                let fullName = "";

                if (employeeId != null) {
                    try {
                        const empRes = await GetAllEmployee();
                        const list = empRes.data?.data ?? empRes.data ?? [];
                        const emp = (Array.isArray(list) ? list : []).find(
                            e => String(e.id ?? e.Id) === String(employeeId)
                        );
                        if (emp) {
                            fullName = emp.fullName ?? emp.FullName
                                ?? `${emp.firstName ?? emp.FirstName ?? ""} ${emp.lastName ?? emp.LastName ?? ""}`.trim();
                        }
                    } catch {
                        // اگه گرفتن لیست کارمندها شکست بخوره، fullName خالی می‌مونه و userName جایگزینش می‌شه
                    }
                }

                if (!alive) return;
                const displayName = fullName || raw.userName || userName;
                setUser({
                    userName: raw.userName ?? raw.UserName ?? userName,
                    employeeId,
                    name: displayName,
                    initials: getInitials(displayName),
                });
            } catch {
                // اگه شکست بخوره، همون "?" پیش‌فرض توی UI باقی می‌مونه
            }
        })();
        return () => { alive = false; };
    }, []);

    return { user };
}

const STAT_DEFS = [
    { key: "productCount", icon: "📦", color: "#F59E0B", lightBg: "#FFFBEB" },
    { key: "orderCount", icon: "📋", color: "#3B82F6", lightBg: "#EFF6FF" },
    { key: "customerCount", icon: "👥", color: "#10B981", lightBg: "#ECFDF5" },
];

const STATUS_COLORS = {
    registered: { bg: "#FFF7ED", color: "#C2410C", dot: "#F97316" },
    confirmed: { bg: "#F0FDF4", color: "#166534", dot: "#22C55E" },
};

function Sk({ w = "100%", h = 14, r = 6 }) {
    return (
        <div style={{
            width: w, height: h, borderRadius: r,
            background: "linear-gradient(90deg,#ede9e2 25%,#f5f2ec 50%,#ede9e2 75%)",
            backgroundSize: "400% 100%", animation: "sk 1.6s ease infinite", display: "inline-block",
        }} />
    );
}

function Badge({ status, t }) {
    const s = STATUS_COLORS[status] || STATUS_COLORS.registered;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: s.bg, color: s.color, fontSize: 11, fontWeight: 600,
            padding: "4px 10px", borderRadius: 99, whiteSpace: "nowrap",
        }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
            {t.statusLabels[status] || status}
        </span>
    );
}

function StatCard({ def, value, delta, t }) {
    return (
        <div style={{
            background: "#fff", borderRadius: 20, padding: "20px",
            border: "1px solid #F0EDE8", display: "flex", flexDirection: "column", gap: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        }}>
            <div style={{
                width: 44, height: 44, borderRadius: 14, background: def.lightBg,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
            }}>{def.icon}</div>
            <div>
                <div style={{ fontSize: 26, fontWeight: 700, color: "#111", letterSpacing: "-0.03em", lineHeight: 1 }}>
                    {value != null ? value.toLocaleString() : <Sk w={55} h={24} />}
                </div>
                <div style={{ fontSize: 12, color: "#9B9890", marginTop: 5, fontWeight: 500 }}>
                    {t.statLabels[def.key]}
                </div>
            </div>
            {delta != null && (
                <div style={{
                    display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700,
                    color: delta >= 0 ? "#16A34A" : "#DC2626",
                    background: delta >= 0 ? "#F0FDF4" : "#FEF2F2",
                    padding: "3px 8px", borderRadius: 99, alignSelf: "flex-start",
                }}>
                    {delta >= 0 ? "↑" : "↓"} {Math.abs(delta)}٪
                </div>
            )}
        </div>
    );
}

// نسخه‌ی کوچک کارت درآمد که هم‌رنگ و هم‌سبک بقیه‌ی کارت‌های آمار، داخل گرید آمار کلی قرار می‌گیرد
function RevenueStatCard({ revenue, growth, t }) {
    return (
        <div style={{
            background: "#fff", borderRadius: 20, padding: "20px",
            border: "1px solid #F0EDE8", display: "flex", flexDirection: "column", gap: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        }}>
            <div style={{
                width: 44, height: 44, borderRadius: 14, background: "#EEF2FF",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
            }}>💰</div>
            <div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#111", letterSpacing: "-0.03em", lineHeight: 1 }}>
                    {revenue != null ? `${revenue.toLocaleString()} IQD` : <Sk w={55} h={22} />}
                </div>
                <div style={{ fontSize: 12, color: "#9B9890", marginTop: 5, fontWeight: 500 }}>
                    {t.monthlyRevenue}
                </div>
            </div>
            {growth != null && (
                <div style={{
                    display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700,
                    color: growth >= 0 ? "#16A34A" : "#DC2626",
                    background: growth >= 0 ? "#F0FDF4" : "#FEF2F2",
                    padding: "3px 8px", borderRadius: 99, alignSelf: "flex-start",
                }}>
                    {growth >= 0 ? "↑" : "↓"} {Math.abs(growth)}٪
                </div>
            )}
        </div>
    );
}

function OrderRow({ order, t }) {
    return (
        <div style={{
            background: "#fff", border: "1px solid #F0EDE8", borderRadius: 16,
            padding: "14px 18px", display: "flex", alignItems: "center", gap: 14,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}>
            <div style={{
                width: 42, height: 42, borderRadius: 12, background: "#F8F6F1",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0,
            }}>📦</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {order?.id ?? <Sk w="50%" h={14} />}
                </div>
                <div style={{ fontSize: 12, color: "#9B9890", marginTop: 3 }}>
                    {order ? `${order.items} ${t.items} · ${order.total?.toLocaleString()} IQD` : <Sk w="35%" h={12} />}
                </div>
            </div>
            {order ? <Badge status={order.status} t={t} /> : <Sk w={70} h={24} r={99} />}
        </div>
    );
}

function SectionTitle({ children, action }) {
    return (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "28px 0 14px" }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: "#111" }}>{children}</span>
            {action && (
                <button onClick={action.onClick} style={{
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: 12, color: "#6366F1", fontWeight: 600, fontFamily: "inherit",
                }}>{action.label}</button>
            )}
        </div>
    );
}

export default function UserDashboard() {
    const width = useWidth();
    const isMobile = width < 640;
    const isTablet = width >= 640 && width < 1024;
    const { stats } = useStats(); // stats.loading و stats.error هم در دسترسن اگر نیاز شد
    const { orders, loading: ordersLoading } = useOrders({ limit: 5 });
    const { user } = useUser();
    const { lang } = useLanguage();
    const t = TEXTS[lang] || TEXTS.fa;
    const cols = isMobile ? 2 : isTablet ? 2 : 4;

    return (
        <div style={{ flex: 1, minWidth: 0, fontFamily: "IRANSansWeb, Candara, Segoe, 'Segoe UI', Optima, Arial, sans-serif" }} dir={lang === "ar" || lang === "fa" ? "rtl" : "ltr"}>
            <div style={{
                background: "#fff", borderBottom: "1px solid #F0EDE8",
                padding: isMobile ? "16px 18px" : "18px 28px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                position: "sticky", top: 0, zIndex: 100,
            }}>
                <div>
                    <div style={{ fontSize: 11, color: "#9B9890", fontWeight: 500, marginBottom: 2 }}>{t.company}</div>
                    <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: "#111" }}>
                        {t.welcome}{user?.name ? `، ${user.name}` : ""} 👋
                    </div>
                </div>
                <div style={{
                    width: 40, height: 40, borderRadius: "50%",
                    background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: 14, color: "#fff",
                    boxShadow: "0 2px 8px rgba(99,102,241,0.3)", flexShrink: 0,
                }}>
                    {user?.initials ?? "?"}
                </div>
            </div>

            <div style={{ padding: isMobile ? "16px 16px 100px" : isTablet ? "22px 24px 40px" : "28px 32px 40px" }}>
                <SectionTitle>{t.stats}</SectionTitle>
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: isMobile ? 12 : 16 }}>
                    {STAT_DEFS.map((def, i) => (
                        <>
                            <StatCard key={def.key} def={def} value={stats?.[def.key] ?? null} delta={stats?.[`${def.key}Delta`] ?? null} t={t} />
                            {i === 1 && (
                                <RevenueStatCard
                                    key="revenue"
                                    revenue={stats?.monthlyRevenue ?? null}
                                    growth={stats?.revenueGrowth ?? null}
                                    t={t}
                                />
                            )}
                        </>
                    ))}
                </div>

                <SectionTitle action={{ label: t.viewAll, onClick: () => {} }}>{t.recentOrders}</SectionTitle>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {ordersLoading
                        ? [1, 2, 3].map(i => (
                            <div key={i} style={{
                                background: "#fff", border: "1px solid #F0EDE8",
                                borderRadius: 16, padding: "14px 18px", display: "flex", gap: 14, alignItems: "center",
                            }}>
                                <Sk w={42} h={42} r={12} />
                                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                                    <Sk w="50%" h={14} /><Sk w="30%" h={12} />
                                </div>
                                <Sk w={70} h={24} r={99} />
                            </div>
                        ))
                        : orders.length === 0
                            ? (
                                <div style={{ textAlign: "center", padding: "48px 20px", color: "#9B9890" }}>
                                    <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: "#444", marginBottom: 5 }}>{t.noOrders}</div>
                                    <div style={{ fontSize: 12 }}>{t.noOrdersSub}</div>
                                </div>
                            )
                            : orders.map(o => <OrderRow key={o.id} order={o} t={t} />)
                    }
                </div>
            </div>
        </div>
    );
}